import type { LensScheduleFilterSet } from '~/hooks/apis/reporting/useLensSchedules';

import type { Lens } from '~/types/leLens';
import { isCardLens } from '~/types/leLens';

import { activeTab, readLensTabs } from '../document/lensTabs';
import { type PageEntry, asLensDocument, isTabs } from '../document/types';

export function reportTabSummary(lens: Lens, selection: LensScheduleFilterSet): string {
  const document = asLensDocument(lens.spec);
  const entries = (page: Array<PageEntry>, prefix = ''): Array<{ id: string; label: string }> =>
    page.flatMap((entry) => {
      if (!isTabs(entry)) return [];
      return entry.tabs.flatMap((tab) => {
        const label = prefix ? `${prefix} / ${tab.tab}` : tab.tab;
        const nested = entries(tab.page, label);
        return nested.length ? nested : [{ id: tab.id, label }];
      });
    });
  if (document) {
    const tabs = entries(document.page);
    if (selection.scope === 'all') return `${Math.max(tabs.length, 1)} tab${tabs.length <= 1 ? '' : 's'}`;
    return tabs
      .filter((tab) => selection.tabs?.includes(tab.id))
      .map((tab) => tab.label)
      .join(', ');
  }
  if (selection.scope === 'all' && isCardLens(lens)) {
    const tabs = lens.spec.filters?.find((filter) => filter.id === 'native_view')?.options ?? [];
    return `${Math.max(tabs.length, 1)} tab${tabs.length <= 1 ? '' : 's'}`;
  }
  return '';
}

export function supportsTabScope(lens: Lens): boolean {
  return lens.nativePage?.pageId === 'luxplus';
}

export function workbookScopeAvailable(lens: Lens): boolean {
  return lens.nativePage?.pageId === 'luxplus';
}

export function selectedReportTabs(lens: Lens): Array<string> {
  const document = asLensDocument(lens.spec);
  if (!document) return [];
  const chosen = readLensTabs();
  const leaves = (page: Array<PageEntry>): Array<string> =>
    page.flatMap((entry) => {
      if (!isTabs(entry)) return [];
      const tab = activeTab(entry, chosen);
      if (!tab) return [];
      const nested = leaves(tab.page);
      return nested.length ? nested : [tab.id];
    });
  return leaves(document.page);
}
